import { useEffect, useState, useCallback, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Award, Trophy, Ban, Clock } from "lucide-react";

/**
 * "My Rewards" — vouchers auto-issued when a season closes. Each row shows
 * the QR (encoding the redemption code) plus the plain-text code so the
 * sponsor can validate either way.
 */
export default function MyRewardsSection() {
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  // iter104 — "Show redeemed" toggle. Default off so the section renders
  // as a clean "what can I redeem right now" list; flipping the switch
  // reveals the player's full trophy cabinet (redeemed history).
  const [showRedeemed, setShowRedeemed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rewards/mine");
      setRewards(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Split live vs redeemed once so both the header counts and the list
  // filter stay in sync. `visibleRewards` is what actually renders.
  const { liveCount, redeemedCount, visibleRewards } = useMemo(() => {
    const live = rewards.filter((r) => !r.redeemed_at);
    const done = rewards.filter((r) => !!r.redeemed_at);
    return {
      liveCount: live.length,
      redeemedCount: done.length,
      visibleRewards: showRedeemed ? rewards : live,
    };
  }, [rewards, showRedeemed]);

  return (
    <section
      className="mt-6 border border-zinc-200 rounded-sm bg-white p-6"
      data-testid="settings-rewards-card"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 inline-flex items-center gap-1.5">
        <Award className="h-3 w-3" /> My rewards
      </p>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">
        Your vouchers.
      </h2>
      <p className="text-sm text-zinc-500 mt-2 leading-relaxed max-w-lg">
        When a season closes, top finishers automatically get a sponsor voucher here.
        Show the QR at the venue to redeem.
      </p>

      {/* iter104 — Show redeemed toggle. Only render the switch if the
          player actually has any redeemed vouchers; otherwise it's dead
          UI. Counts sit inline so the state is legible at a glance. */}
      {redeemedCount > 0 && (
        <div
          className="mt-5 flex items-center justify-between gap-4 flex-wrap border-y border-zinc-200 py-3"
          data-testid="rewards-show-redeemed-row"
        >
          <p className="text-xs text-zinc-600">
            <strong className="text-zinc-950 tabular-nums">{liveCount}</strong> live
            <span className="mx-2 text-zinc-300">·</span>
            <strong className="text-zinc-950 tabular-nums">{redeemedCount}</strong> redeemed
          </p>
          <div className="flex items-center gap-2">
            <Switch
              id="show-redeemed"
              checked={showRedeemed}
              onCheckedChange={setShowRedeemed}
              data-testid="rewards-show-redeemed-toggle"
            />
            <Label
              htmlFor="show-redeemed"
              className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-700 cursor-pointer select-none"
            >
              Show redeemed
            </Label>
          </div>
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</p>
      ) : visibleRewards.length === 0 ? (
        <div
          className="mt-6 border border-dashed border-zinc-300 rounded-sm p-8 text-center"
          data-testid="settings-rewards-empty"
        >
          <Trophy className="h-8 w-8 text-zinc-300 mx-auto" />
          <p className="mt-3 text-sm text-zinc-600">
            {rewards.length === 0
              ? "No rewards yet. Finish in the top spots when the current season closes and yours will appear here."
              : "No live vouchers right now. Flip the switch above to see your trophy cabinet."}
          </p>
        </div>
      ) : (
        <ul className="mt-6 space-y-4" data-testid="settings-rewards-list">
          {visibleRewards.map((r) => {
            const redeemed = !!r.redeemed_at;
            // QR encodes the staff redemption URL (opens straight from any camera).
            const qrValue =
              typeof window !== "undefined"
                ? `${window.location.origin}/redeem/${r.code}`
                : `/redeem/${r.code}`;
            return (
            <li
              key={r.id}
              data-testid={`settings-reward-${r.code}`}
              className={`border rounded-sm p-5 ${
                redeemed
                  ? "border-zinc-200 bg-zinc-50 opacity-80"
                  : "border-amber-200 bg-gradient-to-br from-amber-50 to-white"
              }`}
            >
              <div className="flex flex-col sm:flex-row gap-5">
                {/* QR --------------------------------------------- */}
                <div className="shrink-0 flex flex-col items-center gap-2 w-full sm:w-auto">
                  <div
                    className={`p-2 bg-white border rounded-sm relative w-[min(80vw,320px)] sm:w-[144px] aspect-square ${redeemed ? "border-zinc-300 grayscale" : "border-zinc-200"}`}
                  >
                    <QRCodeSVG
                      value={qrValue}
                      size={512}
                      level="M"
                      className="w-full h-full"
                      data-testid={`reward-qr-${r.code}`}
                    />
                    {redeemed && (
                      <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/40 rounded-sm">
                        <span
                          data-testid={`reward-used-stamp-${r.code}`}
                          className="rotate-[-12deg] px-3 py-1 border-2 border-rose-500 text-rose-500 font-heading font-bold text-lg uppercase tracking-wider bg-white/95"
                        >
                          Used
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 text-center">
                    {redeemed ? "Redeemed" : "Scan at the bar"}
                  </p>
                  {!redeemed && (
                    <p
                      data-testid={`reward-qr-only-notice-${r.code}`}
                      className="text-[11px] text-center leading-snug text-zinc-600 border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 max-w-[min(80vw,320px)] sm:max-w-[220px]"
                    >
                      <strong className="text-zinc-950">Only valid when scanned by the venue.</strong>
                      {" "}
                      The numeric code cannot be entered elsewhere.
                    </p>
                  )}
                </div>

                {/* Details ----------------------------------------- */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] ${
                        redeemed
                          ? "bg-zinc-400 text-white"
                          : r.rank === 1
                          ? "bg-amber-600 text-white"
                          : r.rank === 2
                          ? "bg-zinc-400 text-white"
                          : "bg-amber-800/80 text-white"
                      }`}
                    >
                      <Trophy className="h-3 w-3" />
                      {r.rank === 1 ? "1st place" : r.rank === 2 ? "2nd place" : `${r.rank}rd place`}
                    </span>
                    {redeemed && (
                      <span
                        data-testid={`reward-used-pill-${r.code}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] bg-rose-600 text-white"
                      >
                        <Ban className="h-3 w-3" /> Used
                      </span>
                    )}
                  </div>

                  <p className={`mt-3 font-heading text-2xl font-bold leading-tight ${redeemed ? "line-through text-zinc-500" : "text-zinc-950"}`}>
                    {r.prize_display}
                  </p>
                  {r.sponsor_name ? (
                    <p className="text-sm text-zinc-600 mt-1">
                      Sponsored by <strong>{r.sponsor_name}</strong>
                    </p>
                  ) : null}
                  {r.season_name ? (
                    <p className="text-[11px] text-zinc-400 mt-1 uppercase tracking-[0.15em]">
                      Season · {r.season_name}
                    </p>
                  ) : null}
                  {r.expires_at && !redeemed ? (
                    <p
                      data-testid={`reward-expiry-${r.code}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700"
                    >
                      <Clock className="h-3 w-3" /> Expires {new Date(r.expires_at).toLocaleDateString("en-GB")}
                    </p>
                  ) : null}

                  {redeemed ? (
                    <p className="mt-4 text-sm text-zinc-600 border-t border-zinc-200 pt-3 leading-relaxed">
                      Redeemed{" "}
                      <strong>{new Date(r.redeemed_at).toLocaleString("en-GB")}</strong>
                      {r.redemption_location ? <> at <strong>{r.redemption_location}</strong></> : null}.
                    </p>
                  ) : r.notes ? (
                    <p className="mt-4 text-sm text-zinc-600 border-t border-amber-200 pt-3 leading-relaxed">
                      <strong className="text-zinc-950">How to redeem:</strong> {r.notes}
                    </p>
                  ) : null}
                </div>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
